// Circuito de pádel — the "competing" branch of tournaments, mirroring the
// real 2026 pyramid: Cupra FIP Tour (Bronce → Platino) below Premier Padel
// (P2 → P1 → Major → Finals). Calendar-based: each league season schedules
// the events, the club enters its best pair, players earn ranking points
// and the club earns prize money. The Finals require ranking points — you
// earn your place, division alone is not enough.
//
// Hosting tournaments (TOURNAMENT_DEFINITIONS) is untouched — this is the
// travelling/competing side.

import type { RegionId } from './regionConfig'
import { REGION_ORDER, REGION_META } from './regionConfig'
import { Rng, defaultRng } from '@/game/utils/rng'

export type CircuitTier =
  | 'fip_bronze' | 'fip_silver' | 'fip_gold' | 'fip_platinum' // Cupra FIP Tour
  | 'p2' | 'p1' | 'major' | 'finals'                          // Premier Padel

export interface CircuitTierDefinition {
  tier: CircuitTier
  name: string
  scope: 'regional' | 'nacional' | 'internacional'
  /** Ranking points for the champion pair (per player) */
  championPoints: number
  /** Club prize money for the champion pair */
  prizeChampion: number
  /** Cost of the trip, waived when hostRegion === the club's region */
  travelCost: number
  /** NPC pair strength range */
  npcRatingRange: [number, number]
  /** Bracket size in pairs (power of two) */
  teams: number
  /** Minimum league division to enter (4..1) */
  minDivision: number
  /** Finals only: combined season ranking points the pair must bring */
  minPairPoints?: number
}

export const CIRCUIT_TIERS: Record<CircuitTier, CircuitTierDefinition> = {
  fip_bronze: {
    tier: 'fip_bronze',
    name: 'FIP Bronce',
    scope: 'regional',
    championPoints: 30,
    prizeChampion: 500,
    travelCost: 100,
    npcRatingRange: [25, 45],
    teams: 8,
    minDivision: 4,
  },
  fip_silver: {
    tier: 'fip_silver',
    name: 'FIP Plata',
    scope: 'regional',
    championPoints: 60,
    prizeChampion: 1000,
    travelCost: 200,
    npcRatingRange: [35, 55],
    teams: 8,
    minDivision: 4,
  },
  fip_gold: {
    tier: 'fip_gold',
    name: 'FIP Oro',
    scope: 'nacional',
    championPoints: 125,
    prizeChampion: 2200,
    travelCost: 400,
    npcRatingRange: [45, 70],
    teams: 16,
    minDivision: 3,
  },
  fip_platinum: {
    tier: 'fip_platinum',
    name: 'FIP Platino',
    scope: 'nacional',
    championPoints: 250,
    prizeChampion: 4500,
    travelCost: 600,
    npcRatingRange: [60, 85],
    teams: 16,
    minDivision: 3,
  },
  p2: {
    tier: 'p2',
    name: 'Premier Padel P2',
    scope: 'nacional',
    championPoints: 500,
    prizeChampion: 9000,
    travelCost: 900,
    npcRatingRange: [68, 88],
    teams: 16,
    minDivision: 2,
  },
  p1: {
    tier: 'p1',
    name: 'Premier Padel P1',
    scope: 'internacional',
    championPoints: 1000,
    prizeChampion: 16000,
    travelCost: 1400,
    npcRatingRange: [76, 94],
    teams: 16,
    minDivision: 1,
  },
  major: {
    tier: 'major',
    name: 'Premier Padel Major',
    scope: 'internacional',
    championPoints: 2000,
    prizeChampion: 32000,
    travelCost: 2200,
    npcRatingRange: [84, 99],
    teams: 16,
    minDivision: 1,
  },
  finals: {
    tier: 'finals',
    name: 'Máster Final',
    scope: 'internacional',
    championPoints: 1500,
    prizeChampion: 25000,
    travelCost: 1600,
    npcRatingRange: [88, 99],
    teams: 8,
    minDivision: 1,
    // The pair must have EARNED its invitation this season
    minPairPoints: 800,
  },
}

export const CIRCUIT_TIER_ORDER: CircuitTier[] = [
  'fip_bronze', 'fip_silver', 'p2', 'fip_gold', 'p1', 'fip_platinum', 'major', 'finals',
]

/** League-season weeks (relative to seasonStartWeek) when events happen.
 *  Finals at offset 14 (the last matchday week): the league season rolls at
 *  offset 15 and would wipe an unresolved later slot. */
export const CIRCUIT_WEEK_OFFSETS: Record<CircuitTier, number> = {
  fip_bronze: 2,
  fip_silver: 4,
  p2: 6,
  fip_gold: 8,
  p1: 10,
  fip_platinum: 11,
  major: 13,
  finals: 14,
}

export interface CircuitEventResult {
  /** Rounds won before elimination (== totalRounds when champions) */
  roundsWon: number
  totalRounds: number
  champion: boolean
  points: number // per player of the pair
  prize: number
}

export interface CircuitCalendarSlot {
  id: string
  /** Absolute game week the event resolves */
  week: number
  tier: CircuitTier
  hostRegion: RegionId
  name: string // 'FIP Oro de Gran Capital'
  entered: boolean
  pairIds: string[] | null
  result: CircuitEventResult | null
}

export interface CircuitState {
  /** League season this calendar belongs to */
  season: number
  calendar: CircuitCalendarSlot[]
  /** Rolling season ranking points per club player id */
  ranking: Record<string, number>
  /** Best pair points of past seasons */
  history: Array<{ season: number; points: number }>
}

/** Points by how far the pair got: champion 100%, finalist 60%, then halving-ish. */
const POINT_FRACTIONS = [1, 0.6, 0.3, 0.12, 0.05, 0.02]

export function pointsForRun(def: CircuitTierDefinition, roundsWon: number, totalRounds: number): number {
  const fromEnd = totalRounds - roundsWon
  const fraction = POINT_FRACTIONS[Math.min(fromEnd, POINT_FRACTIONS.length - 1)]
  return Math.round(def.championPoints * fraction)
}

/** Generate the four FIP slots for a league season (deterministic per rng) */
export function generateCircuitCalendar(
  season: number,
  seasonStartWeek: number,
  rng: Rng = defaultRng,
): CircuitCalendarSlot[] {
  return CIRCUIT_TIER_ORDER.map(tier => {
    const def = CIRCUIT_TIERS[tier]
    const hostRegion = REGION_ORDER[Math.floor(rng() * REGION_ORDER.length)]
    return {
      id: `circuit-s${season}-${tier}`,
      week: seasonStartWeek + CIRCUIT_WEEK_OFFSETS[tier],
      tier,
      hostRegion,
      name: `${def.name} de ${REGION_META[hostRegion].label}`,
      entered: false,
      pairIds: null,
      result: null,
    }
  })
}

/** Effective travel cost for the club (home events are free) */
export function getTravelCost(slot: CircuitCalendarSlot, clubRegion: RegionId): number {
  return slot.hostRegion === clubRegion ? 0 : CIRCUIT_TIERS[slot.tier].travelCost
}
